import { ZeroAddress } from 'ethers'

import { getDcaApiBase } from './env'
import {
  DcaCancelPrepareResponse,
  DcaConfig,
  DcaExecution,
  DcaOrderDetail,
  DcaOrderParams,
  DcaOrdersResponse,
  DcaPrepareResponse,
  DcaPreview,
  DcaSubmittedResponse
} from './types'

const REQUEST_TIMEOUT = 20000
const CONFIG_TTL = 5 * 60 * 1000

const NATIVE_ALIAS = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'

export const isDcaNativeAddress = (address: string | null | undefined) =>
  !!address && [ZeroAddress, NATIVE_ALIAS].includes(address.toLowerCase())

/**
 * A failed `/dca/*` call. `code` / `reason` are the backend's machine-readable
 * fields (`DCA_INVALID_REQUEST` + `AMOUNT_BELOW_MINIMUM`, …); its `message` is
 * debug-only by contract, so the UI translates from `code` / `reason` instead.
 */
export class DcaApiError extends Error {
  status: number

  code: string | null

  reason: string | null

  constructor({
    status,
    code,
    reason,
    message
  }: {
    status: number
    code?: string | null
    reason?: string | null
    message: string
  }) {
    super(message)
    this.name = 'DcaApiError'
    this.status = status
    this.code = code ?? null
    this.reason = reason ?? null
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  let response: Response
  try {
    response = await fetch(`${getDcaApiBase()}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
      signal: controller.signal
    })
  } catch (error: any) {
    throw new DcaApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: error?.name === 'AbortError' ? 'DCA API request timed out' : String(error?.message)
    })
  } finally {
    clearTimeout(timeout)
  }

  const body: any = await response.json().catch(() => null)

  if (!response.ok) {
    // Business errors: { code, reason, message }. DTO validation (NestJS):
    // { statusCode, error, message: string[] }.
    const message = Array.isArray(body?.message)
      ? body.message.join('; ')
      : body?.message || `DCA API ${response.status}`
    throw new DcaApiError({
      status: response.status,
      code: body?.code ?? (response.status === 400 ? 'VALIDATION_ERROR' : null),
      reason: body?.reason ?? null,
      message
    })
  }

  return body as T
}

let configCache: { value: DcaConfig; at: number } | null = null

export async function fetchDcaConfig({ force = false } = {}): Promise<DcaConfig> {
  if (!force && configCache && Date.now() - configCache.at < CONFIG_TTL) return configCache.value

  const value = await request<DcaConfig>('/dca/config')
  configCache = { value, at: Date.now() }
  return value
}

const toOrderBody = (params: DcaOrderParams) => ({
  owner: params.owner,
  // The API takes either spelling of the native coin; send one.
  tokenIn: isDcaNativeAddress(params.tokenIn) ? ZeroAddress : params.tokenIn,
  tokenOut: params.tokenOut,
  // The total, never `amountPerExecutionRaw` as well — sending both is rejected
  // with INVALID_AMOUNT.
  totalAmountRaw: params.totalAmountRaw,
  totalExecutions: params.totalExecutions,
  interval: params.interval,
  ...(params.startAt ? { startAt: params.startAt } : {}),
  ...(params.maxSlippageBps !== undefined ? { maxSlippageBps: params.maxSlippageBps } : {})
})

/**
 * Validate an order and quote its route, without the transactions. Unlike
 * `/prepare` it still answers when the slippage is below the route minimum,
 * with `requiredSlippageBps` / `suggestedSlippageBps` to fix it.
 */
export function previewDcaOrder(params: DcaOrderParams): Promise<DcaPreview> {
  return request<DcaPreview>('/dca/orders/preview', {
    method: 'POST',
    body: JSON.stringify(toOrderBody(params))
  })
}

/**
 * Validate an order and get the transactions that create it. Read-only on the
 * backend, so the form also uses it as its live preview: its `summary` is the
 * preview plus the allowance the plan approves.
 */
export function prepareDcaOrder(params: DcaOrderParams): Promise<DcaPrepareResponse> {
  return request<DcaPrepareResponse>('/dca/orders/prepare', {
    method: 'POST',
    body: JSON.stringify(toOrderBody(params))
  })
}

/**
 * Tell the backend an order tx (create / cancel) landed, so it shows as pending
 * before the indexer, which waits 12 blocks, picks it up.
 */
export function submitDcaOrderTx(txHash: string, owner: string): Promise<DcaSubmittedResponse> {
  return request<DcaSubmittedResponse>('/dca/orders/submitted', {
    method: 'POST',
    headers: { 'Idempotency-Key': txHash },
    body: JSON.stringify({ txHash, owner })
  })
}

/** The API's page cap. Owners with more orders than this see the newest ones. */
const ORDERS_PAGE_SIZE = 100

/** Every order of `owner`, newest first, plus the txs not indexed yet. */
export function fetchDcaOrders(owner: string): Promise<DcaOrdersResponse> {
  const query = new URLSearchParams({ owner, page: '0', pageSize: String(ORDERS_PAGE_SIZE) })
  return request<DcaOrdersResponse>(`/dca/orders?${query.toString()}`)
}

/** 404 right after creation, until the indexer has seen the order. */
export function fetchDcaOrder(orderId: string): Promise<DcaOrderDetail> {
  return request<DcaOrderDetail>(`/dca/orders/${encodeURIComponent(orderId)}`)
}

/** Newest first. */
export async function fetchDcaExecutions(orderId: string): Promise<DcaExecution[]> {
  const { executions } = await request<{ executions: DcaExecution[] }>(
    `/dca/orders/${encodeURIComponent(orderId)}/executions?limit=200`
  )
  return executions
}

/** Steps: `cancel-order`, then an `approve` that lowers the allowance. */
export function prepareDcaCancel(
  orderId: string,
  owner: string
): Promise<DcaCancelPrepareResponse> {
  return request<DcaCancelPrepareResponse>(
    `/dca/orders/${encodeURIComponent(orderId)}/cancel/prepare`,
    { method: 'POST', body: JSON.stringify({ owner }) }
  )
}

/**
 * Public price proxy the swap screen and the gas tank already use for tokens
 * the portfolio cannot price. A CoinGecko passthrough keyed by Gecko platform.
 */
const PRICE_PROXY_URL = 'https://apis.zkxwallet.com/cena/api/v3/simple/token_price/pulsechain'
// Cold answers take 2–3s and have been seen past 5s.
const PRICE_TIMEOUT = 10000

/**
 * USD price of each PulseChain token, by lowercased address. Tokens the proxy
 * cannot price are left out. The portfolio only prices what the account holds,
 * so without this the form shows no fiat value for a token it does not hold.
 */
export async function fetchDcaUsdPrices(addresses: string[]): Promise<Record<string, number>> {
  const unique = [...new Set(addresses.map((a) => a.toLowerCase()))]
  if (!unique.length) return {}

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), PRICE_TIMEOUT)
  try {
    const query = new URLSearchParams({
      contract_addresses: unique.join(','),
      vs_currencies: 'usd'
    })
    const response = await fetch(`${PRICE_PROXY_URL}?${query.toString()}`, {
      signal: controller.signal
    })
    if (!response.ok) throw new Error(`price proxy responded with ${response.status}`)
    const body: any = await response.json()

    const prices: Record<string, number> = {}
    unique.forEach((address) => {
      const price = Number(body?.[address]?.usd)
      if (Number.isFinite(price) && price > 0) prices[address] = price
    })
    return prices
  } finally {
    clearTimeout(timeout)
  }
}
